import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { stockAnalyticsService, StockAnalytics as Analytics } from '../../api/services';

const fmt = (n: number) =>
  new Intl.NumberFormat('fr-TN', { minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(n ?? 0);

const ABC_COLOR: Record<string, string> = { A: '#4f46e5', B: '#0d9488', C: '#9ca3af' };

const StockAnalyticsPage: React.FC = () => {
  const [data, setData] = useState<Analytics | null>(null);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    stockAnalyticsService.get()
      .then(setData)
      .catch((e) => toast.error(e?.response?.data?.message || 'Erreur'))
      .finally(() => setBusy(false));
  }, []);

  const abcCounts = useMemo(() => {
    const c = { A: 0, B: 0, C: 0 };
    data?.abc.forEach((x) => { c[x.classe] += 1; });
    return c;
  }, [data]);

  if (busy) return <p className="p-4 text-sm text-gray-500">Chargement de l’analytique…</p>;
  if (!data) return <p className="p-4 text-sm text-gray-500">Aucune donnée.</p>;

  const catData = data.valorisationParCategorie.map((c) => ({ name: c.categorie, valeur: c.valeur }));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Analytique du stock</h1>
        <p className="text-sm text-gray-500">
          Valorisation, rotation, analyse ABC et stock dormant (fenêtre {data.windowDays} j).
        </p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="Valorisation du stock" value={`${fmt(data.valorisationTotale)} DT`} />
        <Kpi label="Coût marchandises vendues" value={`${fmt(data.coutMarchandisesVendues)} DT`} />
        <Kpi label="Rotation" value={`${data.rotation.toFixed(2)}×`} />
        <Kpi label="Articles suivis" value={String(data.totalArticles)} />
      </div>

      {/* Valorisation par catégorie */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <h2 className="text-sm font-semibold mb-3">Valorisation par catégorie</h2>
        {catData.length === 0 ? (
          <p className="text-sm text-gray-500">Aucune donnée.</p>
        ) : (
          <div style={{ width: '100%', height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={catData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip formatter={(v: any) => `${fmt(Number(v))} DT`} />
                <Bar dataKey="valeur" fill="#4f46e5" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* ABC */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold">Analyse ABC (valeur consommée)</h2>
          <div className="flex gap-2 text-xs">
            {(['A', 'B', 'C'] as const).map((k) => (
              <span key={k} className="px-2 py-0.5 rounded-full font-semibold text-white"
                style={{ background: ABC_COLOR[k] }}>
                {k} : {abcCounts[k]}
              </span>
            ))}
          </div>
        </div>
        {data.abc.length === 0 ? (
          <p className="text-sm text-gray-500">Aucune consommation sur la période.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                <tr>
                  <th className="px-3 py-2 text-left">Classe</th>
                  <th className="px-3 py-2 text-left">Article</th>
                  <th className="px-3 py-2 text-right">Valeur consommée</th>
                  <th className="px-3 py-2 text-right">Part</th>
                  <th className="px-3 py-2 text-right">Cumul</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {data.abc.slice(0, 30).map((x) => (
                  <tr key={x.articleId} className="hover:bg-gray-50">
                    <td className="px-3 py-2">
                      <span className="px-2 py-0.5 rounded-full text-white text-xs font-semibold"
                        style={{ background: ABC_COLOR[x.classe] }}>{x.classe}</span>
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-medium">{x.reference}</div>
                      <div className="text-xs text-gray-500">{x.designation}</div>
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{fmt(x.valeurConsommee)}</td>
                    <td className="px-3 py-2 text-right text-xs">{x.partPct.toFixed(1)}%</td>
                    <td className="px-3 py-2 text-right text-xs">{x.cumulPct.toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Stock dormant */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <h2 className="text-sm font-semibold mb-3">
          Stock dormant ({data.dormant.length}) — aucune sortie depuis {data.dormantDays} j
        </h2>
        {data.dormant.length === 0 ? (
          <p className="text-sm text-gray-500">Aucun stock dormant 🎉</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                <tr>
                  <th className="px-3 py-2 text-left">Article</th>
                  <th className="px-3 py-2 text-right">Stock</th>
                  <th className="px-3 py-2 text-right">Valeur immobilisée</th>
                  <th className="px-3 py-2 text-right">Sans mouvement</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {data.dormant.map((x) => (
                  <tr key={x.articleId} className="hover:bg-gray-50">
                    <td className="px-3 py-2">
                      <div className="font-medium">{x.reference}</div>
                      <div className="text-xs text-gray-500">{x.designation}</div>
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{x.stockActuel}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmt(x.valeur)} DT</td>
                    <td className="px-3 py-2 text-right text-xs text-gray-500">
                      {x.joursSansMouvement == null ? `> ${data.dormantDays} j` : `${x.joursSansMouvement} j`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const Kpi: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-4">
    <div className="text-xs text-gray-500">{label}</div>
    <div className="mt-1 text-xl font-bold text-gray-900">{value}</div>
  </div>
);

export default StockAnalyticsPage;
